import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import PageHeader from '@/components/PageHeader';
import { AdminPageSections, AutoRefreshToolbar, BulkActionBar, BulkActionButton, CollapsiblePanel } from '@/components/common';
import CleanupActivityConfirmModal from '@/components/activity/CleanupActivityConfirmModal';
import CleanupDeliveriesConfirmModal from '@/components/activity/CleanupDeliveriesConfirmModal';
import DeliveryDetailDrawer from '@/components/activity/DeliveryDetailDrawer';
import ActivityLogPanel from '@/components/activity/ActivityLogPanel';
import Pagination from '@/components/Pagination';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import { formatTableError } from '@/components/table/formatCellValue';
import {
  DeliveryStatusBadge,
  DiscoveredLinkStatusBadge,
  EmptyState,
  ErrorBanner,
  FilterSelect,
  MetricChip,
  RecipientCell,
  SourceChips,
  TableRowActions,
  Toolbar,
  ExportButtons,
} from '@/components/ui';
import ListingUnknownBadges from '@/components/ui/ListingUnknownBadges';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import type { Delivery, DeliveryDetail, DiscoveredLink, Recipient, Search } from '@/types';
import { formatTelegramError } from '@/utils/telegramErrors';
import { formatApiError } from '@/utils/formatApiError';
import { formatDeliveryError } from '@/utils/lifecycleReasons';
import { deliveryRowKey, resolveBulkRetryDeliveryIds } from '@/utils/deliveryRetryIds';
import { filterValidUuids, isUuid } from '@/utils/uuid';
import AppConfirmDialog from '@/ui/components/AppConfirmDialog';
import { SOURCE_FILTER_OPTIONS } from '@/utils/sourceFilterOptions';

type DeliveryStats = {
  sent: number;
  failed: number;
  skipped: number;
  lastDelivery: string | null;
};

export default function ActivityPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const navigate = useNavigate();
  const canManageJobs = me?.permissions.canManageJobs ?? false;
  const bulk = useBulkSelection<Delivery>();
  const fetchGenerationRef = useRef(0);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [showHistoryCleanupModal, setShowHistoryCleanupModal] = useState(false);
  const [alsoDeleteRelatedJobs, setAlsoDeleteRelatedJobs] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [detailDeliveryId, setDetailDeliveryId] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [links, setLinks] = useState<DiscoveredLink[]>([]);
  const [searches, setSearches] = useState<Search[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [linkPage, setLinkPage] = useState(1);
  const [deliveryMeta, setDeliveryMeta] = useState({ pages: 1, total: 0 });
  const [linkMeta, setLinkMeta] = useState({ pages: 1, total: 0 });
  const [stats, setStats] = useState<DeliveryStats>({ sent: 0, failed: 0, skipped: 0, lastDelivery: null });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [pendingClearDelivery, setPendingClearDelivery] = useState<Delivery | null>(null);
  const [clearDeliveryBusy, setClearDeliveryBusy] = useState(false);
  const [clearDeliveryError, setClearDeliveryError] = useState('');

  useEffect(() => {
    Promise.all([api.searches.list(), api.recipients.list()])
      .then(([s, r]) => {
        setSearches(s);
        setRecipients(r);
      })
      .catch(() => {});
  }, []);

  const loadStats = useCallback(async () => {
    const [sent, failed, skipped, recent] = await Promise.all([
      api.deliveriesPaginated(1, 1, { status: 'SENT' }),
      api.deliveriesPaginated(1, 1, { status: 'FAILED' }),
      api.deliveriesPaginated(1, 1, { status: 'SKIPPED' }),
      api.deliveriesPaginated(1, 1),
    ]);
    setStats({
      sent: sent.total,
      failed: failed.total,
      skipped: skipped.total,
      lastDelivery: recent.items[0]?.sent_at ?? null,
    });
  }, []);

  const loadDeliveries = useCallback(
    async (page: number) => {
      const generation = fetchGenerationRef.current;
      const sentAfter = dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined;
      const sentBefore = dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined;
      const d = await api.deliveriesPaginated(page, 20, {
        status: statusFilter || undefined,
        search_id: searchFilter || undefined,
        recipient_id: recipientFilter || undefined,
        source: sourceFilter || undefined,
        q: textFilter.trim() || undefined,
        sent_after: sentAfter,
        sent_before: sentBefore,
      });
      if (generation !== fetchGenerationRef.current) return;
      setDeliveries(d.items);
      setDeliveryMeta({ pages: d.pages, total: d.total });
    },
    [statusFilter, searchFilter, recipientFilter, sourceFilter, textFilter, dateFrom, dateTo]
  );

  const loadLinks = useCallback(async (page: number) => {
    const generation = fetchGenerationRef.current;
    const l = await api.discoveredLinksPaginated(page, 20);
    if (generation !== fetchGenerationRef.current) return;
    setLinks(l.items);
    setLinkMeta({ pages: l.pages, total: l.total });
  }, []);

  const fetchActivity = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    await Promise.all([loadDeliveries(deliveryPage), loadLinks(linkPage), loadStats()]);
    if (generation !== fetchGenerationRef.current) return;
    setError('');
  }, [deliveryPage, linkPage, loadDeliveries, loadLinks, loadStats]);

  const clearActivityState = useCallback(() => {
    fetchGenerationRef.current += 1;
    setDeliveries([]);
    setLinks([]);
    setDeliveryMeta({ pages: 1, total: 0 });
    setLinkMeta({ pages: 1, total: 0 });
    setStats({ sent: 0, failed: 0, skipped: 0, lastDelivery: null });
    setDetailDeliveryId(null);
    setError('');
    bulk.clearSelection();
  }, [bulk]);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => deliveries.length > 0 || links.length > 0,
    onWorkspaceChange: clearActivityState,
    load: async () => {
      try {
        await fetchActivity();
      } catch (e) {
        const message = formatApiError(e, 'Failed to load activity');
        setError(message);
        throw e instanceof Error ? e : new Error(message);
      }
    },
    autoRefresh: {
      intervalMs: 15000,
      deps: [deliveryPage, linkPage, loadDeliveries, loadLinks, loadStats],
      storageKey: 'activity-auto-refresh',
    },
  });

  const hasActiveFilters = Boolean(
    statusFilter || searchFilter || recipientFilter || sourceFilter || textFilter || dateFrom || dateTo
  );

  const deliveryExportParams = useMemo(() => {
    const sentAfter = dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined;
    const sentBefore = dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined;
    return {
      status: statusFilter || undefined,
      search_id: searchFilter || undefined,
      recipient_id: recipientFilter || undefined,
      source: sourceFilter || undefined,
      q: textFilter.trim() || undefined,
      sent_after: sentAfter,
      sent_before: sentBefore,
    };
  }, [statusFilter, searchFilter, recipientFilter, sourceFilter, textFilter, dateFrom, dateTo]);

  const isRetryable = useCallback(
    (d: Delivery) => (d.status === 'FAILED' || d.status === 'SKIPPED') && Boolean(d.subscription_id),
    []
  );

  const lastDeliveryLabel = useMemo(() => {
    if (!stats.lastDelivery) return '—';
    return new Date(stats.lastDelivery).toLocaleString();
  }, [stats.lastDelivery]);

  const retryDelivery = useCallback(
    async (d: Delivery | DeliveryDetail) => {
      if (!isUuid(d.id)) {
        toast('Invalid delivery ID', 'error');
        return;
      }
      if (!isRetryable(d as Delivery)) {
        toast('Only failed or skipped deliveries can be retried', 'error');
        return;
      }
      setRetryingId(d.id);
      try {
        const result = await api.retryDelivery(d.id);
        if (result.success) {
          toast(result.message || 'Retry queued for send worker', 'success');
        } else if (result.skipped) {
          toast(result.message || 'Delivery was not retried', 'info');
        } else {
          toast(result.message || 'Retry failed', 'error');
        }
        await Promise.all([loadDeliveries(deliveryPage), loadStats()]);
      } catch (err) {
        toast(formatApiError(err, 'Retry failed'), 'error');
      } finally {
        setRetryingId(null);
      }
    },
    [toast, loadDeliveries, loadStats, deliveryPage, isRetryable]
  );

  const copyUrl = useCallback(
    (url: string) => {
      navigator.clipboard.writeText(url).then(
        () => toast('URL copied', 'success'),
        () => toast('Copy failed', 'error')
      );
    },
    [toast]
  );

  const viewRelatedJob = useCallback(
    async (delivery: Delivery) => {
      try {
        const related = await api.relatedJobForDelivery(delivery.id);
        if (!related) {
          toast('No linked job found for this delivery', 'info');
          return;
        }
        navigate(`/jobs?job_id=${related.job_id}`);
      } catch (e) {
        toast(formatApiError(e, 'Failed to find related job'), 'error');
      }
    },
    [navigate, toast]
  );

  const clearSingleDelivery = useCallback(
    async (delivery: Delivery, alsoJobs: boolean) => {
      const result = await api.bulkDeleteDeliveries([delivery.id], {
        alsoDeleteRelatedJobs: alsoJobs,
      });
      const deleted = result.deleted ?? 0;
      const jobsDeleted = result.jobs_deleted ?? 0;
      if ((result.failed ?? 0) > 0) {
        toast(`Removed ${deleted} log(s); related job could not be removed`, 'info');
      } else if (jobsDeleted > 0) {
        toast(`Removed delivery log and ${jobsDeleted} related job(s)`, 'success');
      } else {
        toast(`Removed ${deleted} delivery log(s)`, 'success');
      }
      await Promise.all([loadDeliveries(deliveryPage), loadStats()]);
    },
    [deliveryPage, loadDeliveries, loadStats, toast]
  );

  const deliveryPageKeys = useMemo(
    () => filterValidUuids(deliveries.map((d) => d.id)),
    [deliveries]
  );

  const runBulkRetry = useCallback(async () => {
    const ids = resolveBulkRetryDeliveryIds(deliveries, bulk.selectedKeys, isRetryable);
    if (!ids.length) {
      if (bulk.selectedCount > 0) {
        toast('Selected rows do not have valid delivery IDs to retry', 'error');
      } else {
        toast('Select failed or skipped deliveries to retry', 'info');
      }
      return;
    }
    setBulkBusy(true);
    try {
      const result = await api.bulkRetryDeliveries(ids);
      if (result.retried > 0) {
        toast(`Queued ${result.retried} deliver${result.retried === 1 ? 'y' : 'ies'} for retry`, 'success');
      }
      if (result.skipped > 0) {
        toast(`${result.skipped} already sent or in progress`, 'info');
      }
      if (result.failed > 0) {
        toast(`${result.failed} could not be retried`, 'error');
      }
      bulk.clearSelection();
      await Promise.all([loadDeliveries(deliveryPage), loadStats()]);
    } catch (e) {
      toast(formatApiError(e, 'Bulk retry failed'), 'error');
      throw e;
    } finally {
      setBulkBusy(false);
    }
  }, [bulk, deliveries, deliveryPage, isRetryable, loadDeliveries, loadStats, toast]);

  const runHistoryCleanup = useCallback(
    async (opts: { olderThanDays: number; status: string; alsoDeleteRelatedJobs: boolean }) => {
      const result = await api.cleanupDeliveries({
        older_than_days: opts.olderThanDays,
        status: opts.status,
        also_delete_related_jobs: opts.alsoDeleteRelatedJobs,
      });
      toast(`Removed ${result.deleted ?? 0} delivery log(s)`, 'success');
      await Promise.all([loadDeliveries(deliveryPage), loadStats()]);
    },
    [deliveryPage, loadDeliveries, loadStats, toast]
  );

  const runBulkDelete = useCallback(async () => {
    const ids = filterValidUuids(Array.from(bulk.selectedKeys));
    if (!ids.length) {
      toast('Selected rows do not have valid delivery IDs', 'error');
      return;
    }
    setBulkBusy(true);
    try {
      const result = await api.bulkDeleteDeliveries(ids, {
        alsoDeleteRelatedJobs: alsoDeleteRelatedJobs,
      });
      const deleted = result.deleted ?? 0;
      const jobsDeleted = result.jobs_deleted ?? 0;
      const failed = result.failed ?? 0;
      if (failed > 0) {
        toast(`Removed ${deleted} log(s); ${failed} related job(s) could not be removed`, 'info');
      } else if (jobsDeleted > 0) {
        toast(`Removed ${deleted} log(s) and ${jobsDeleted} related job(s)`, 'success');
      } else {
        toast(`Removed ${deleted} delivery log(s)`, 'success');
      }
      bulk.clearSelection();
      await Promise.all([loadDeliveries(deliveryPage), loadStats()]);
    } catch (e) {
      toast(formatApiError(e, 'Delete failed'), 'error');
      throw e;
    } finally {
      setBulkBusy(false);
    }
  }, [alsoDeleteRelatedJobs, bulk, deliveryPage, loadDeliveries, loadStats, toast]);

  const deliveriesEmptyState = (
    <EmptyState
      title={hasActiveFilters ? 'No deliveries match your filters' : 'No deliveries yet'}
      description={
        hasActiveFilters
          ? 'Try clearing filters or check a different status.'
          : 'Deliveries appear here after the send worker notifies recipients about new listings.'
      }
      action={
        !hasActiveFilters ? (
          <Link to="/subscriptions" className="btn btn-sm">
            Set up a subscription
          </Link>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              setStatusFilter('');
              setSearchFilter('');
              setRecipientFilter('');
              setSourceFilter('');
              setTextFilter('');
              setDateFrom('');
              setDateTo('');
              setDeliveryPage(1);
            }}
          >
            Clear filters
          </button>
        )
      }
    />
  );

  const deliveryColumns = useMemo<DataTableColumn<Delivery>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        width: 150,
        minWidth: 120,
        maxWidth: 200,
        sortValue: (d) => d.sent_at,
        csvValue: (d) => new Date(d.sent_at).toLocaleString(),
        render: (d) => (
          <TruncatedCell value={new Date(d.sent_at).toLocaleString()} title={d.sent_at} />
        ),
      },
      {
        id: 'search',
        header: 'Search',
        width: 150,
        minWidth: 100,
        maxWidth: 260,
        sortValue: (d) => d.search_name,
        csvValue: (d) => d.search_name,
        render: (d) =>
          d.search_id && d.search_name ? (
            <Link to={`/searches/${d.search_id}/links`} className="table-link">
              <TruncatedCell value={d.search_name} />
            </Link>
          ) : (
            <TruncatedCell value={d.search_name} />
          ),
      },
      {
        id: 'recipient',
        header: 'Recipient',
        width: 130,
        minWidth: 100,
        maxWidth: 180,
        sortValue: (d) => d.recipient_display_name || d.chat_id,
        csvValue: (d) => d.recipient_display_name || d.chat_id,
        render: (d) => (
          <RecipientCell
            recipient={{
              display_name: d.recipient_display_name,
              telegram_username: d.recipient_telegram_username,
              chat_id: d.chat_id,
              telegram_resolution_method: d.recipient_resolution_method,
            }}
            linkTo={d.recipient_id ? '/recipients' : undefined}
          />
        ),
      },
      {
        id: 'channel',
        header: 'Channel',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        sortValue: (d) => d.channel,
        csvValue: (d) => `${d.channel} ${d.chat_id || ''}`.trim(),
        render: (d) => (
          <span title={d.chat_id || undefined}>
            {d.channel}
            {d.chat_id ? ` · ${d.chat_id}` : ''}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: 90,
        minWidth: 72,
        maxWidth: 110,
        resizable: false,
        sortValue: (d) => d.status,
        csvValue: (d) => d.status,
        render: (d) => <DeliveryStatusBadge status={d.status} />,
      },
      {
        id: 'source',
        header: 'Source',
        width: 110,
        minWidth: 90,
        maxWidth: 150,
        sortValue: (d) => d.source_name,
        csvValue: (d) => d.source_name,
        render: (d) =>
          d.source_name ? <SourceChips sources={[d.source_name]} variant="compact" /> : <span className="muted">—</span>,
      },
      {
        id: 'metadata',
        header: 'Metadata',
        width: 160,
        minWidth: 120,
        maxWidth: 240,
        sortValue: (d) => (d.listing_unknown_fields ?? []).join(', '),
        csvValue: (d) => (d.listing_unknown_fields ?? []).join('; '),
        render: (d) => (
          <ListingUnknownBadges fields={d.listing_unknown_fields} />
        ),
      },
      {
        id: 'error',
        header: 'Error',
        width: 180,
        minWidth: 120,
        maxWidth: 320,
        sortValue: (d) => d.friendly_error_message ?? d.error_message,
        csvValue: (d) => d.friendly_error_message ?? d.error_message,
        render: (d) => {
          if ((d.status !== 'FAILED' && d.status !== 'SKIPPED') || !d.error_message) {
            return '—';
          }
          const friendly = formatDeliveryError(d.error_message, d.friendly_error_message);
          const msg = formatTableError(friendly);
          return (
            <TruncatedCell value={msg === '—' ? msg : formatTelegramError(msg)} />
          );
        },
      },
      {
        id: 'url',
        header: 'URL',
        width: 200,
        minWidth: 160,
        maxWidth: 400,
        sortValue: (d) => d.url,
        csvValue: (d) => d.url,
        render: (d) => <TruncatedCell value={d.url} href={d.url || undefined} copyable />,
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 280,
        minWidth: 280,
        maxWidth: 340,
        resizable: false,
        actions: true,
        render: (d) => (
          <TableRowActions
            url={d.url || undefined}
            onCopy={d.url ? () => copyUrl(d.url!) : undefined}
            onRetry={isRetryable(d) ? () => retryDelivery(d) : undefined}
            retrying={retryingId === d.id}
            retryDisabled={!isRetryable(d)}
            retryTitle={
              isRetryable(d)
                ? 'Retry via send worker (safe — will not duplicate sent deliveries)'
                : d.status === 'SENT'
                  ? 'Already sent'
                  : 'Retry unavailable'
            }
          >
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                if (!isUuid(d.id)) {
                  toast('This delivery has no valid ID and cannot be opened', 'error');
                  return;
                }
                setDetailDeliveryId(d.id);
              }}
            >
              Details
            </button>
            {canManageJobs ? (
              <>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => void viewRelatedJob(d)}>
                  View job
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    setClearDeliveryError('');
                    setPendingClearDelivery(d);
                  }}
                >
                  Clear log
                </button>
              </>
            ) : null}
          </TableRowActions>
        ),
      },
    ],
    [canManageJobs, copyUrl, isRetryable, retryDelivery, retryingId, toast, viewRelatedJob]
  );

  const linkColumns = useMemo<DataTableColumn<DiscoveredLink>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        width: 150,
        minWidth: 120,
        maxWidth: 200,
        render: (l) => (
          <TruncatedCell value={new Date(l.discovered_at).toLocaleString()} title={l.discovered_at} />
        ),
      },
      {
        id: 'source',
        header: 'Source',
        width: 130,
        minWidth: 100,
        maxWidth: 200,
        render: (l) =>
          l.source_name ? <SourceChips sources={[l.source_name]} variant="compact" /> : <span className="muted">—</span>,
      },
      {
        id: 'title',
        header: 'Title',
        width: 220,
        minWidth: 140,
        maxWidth: 400,
        render: (l) => <TruncatedCell value={l.title} />,
      },
      {
        id: 'status',
        header: 'Status',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        resizable: false,
        sortValue: (l) => (l.is_new ? 'NEW' : 'DUPLICATE'),
        csvValue: (l) => (l.is_new ? 'NEW' : 'DUPLICATE'),
        render: (l) => <DiscoveredLinkStatusBadge isNew={l.is_new} />,
      },
      {
        id: 'url',
        header: 'URL',
        width: 280,
        minWidth: 180,
        maxWidth: 560,
        render: (l) => <TruncatedCell value={l.url} href={l.url} copyable />,
      },
    ],
    []
  );

  const linksEmptyState = (
    <EmptyState
      title="No links discovered yet"
      description="Run a scrape from Searches or wait for the collector to find new listings."
      action={
        <Link to="/searches" className="btn btn-sm">
          Go to searches
        </Link>
      }
    />
  );

  return (
    <div>
      <PageHeader title="Activity" subtitle="Notification history, delivery retries, and discovered listings">
        {canManageJobs ? (
          <button type="button" className="btn btn-ghost" onClick={() => setShowHistoryCleanupModal(true)}>
            Clear old history
          </button>
        ) : null}
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 15s" />
      </PageHeader>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {autoRefresh.error ? <ErrorBanner message={autoRefresh.error} /> : null}

      <AdminPageSections>
        <CollapsiblePanel id="stats" storageScope="activity-page" title="Delivery stats" defaultExpanded>
      <div className="metric-chips">
        <MetricChip label="Sent" value={stats.sent} variant="success" />
        <MetricChip label="Failed" value={stats.failed} variant="danger" />
        <MetricChip label="Skipped" value={stats.skipped} variant="warning" />
        <MetricChip label="Last delivery" value={lastDeliveryLabel} />
      </div>
        </CollapsiblePanel>

      <CollapsiblePanel
        id="deliveries"
        storageScope="activity-page"
        title="Notification history"
        subtitle="Sent, failed, and skipped Telegram deliveries"
        defaultExpanded
      >
        <Toolbar>
          <FilterSelect label="Status" htmlFor="activity-status">
            <select
              id="activity-status"
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setDeliveryPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="SKIPPED">Skipped</option>
            </select>
          </FilterSelect>
          <FilterSelect label="Search" htmlFor="activity-search">
            <select
              id="activity-search"
              className="filter-select"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setDeliveryPage(1);
              }}
            >
              <option value="">All searches</option>
              {searches.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="Recipient" htmlFor="activity-recipient">
            <select
              id="activity-recipient"
              className="filter-select"
              value={recipientFilter}
              onChange={(e) => {
                setRecipientFilter(e.target.value);
                setDeliveryPage(1);
              }}
            >
              <option value="">All recipients</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.display_name || r.chat_id}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="Source" htmlFor="activity-source">
            <select
              id="activity-source"
              className="filter-select"
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setDeliveryPage(1);
              }}
            >
              {SOURCE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="From" htmlFor="activity-date-from">
            <input
              id="activity-date-from"
              type="date"
              className="filter-select"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setDeliveryPage(1);
              }}
            />
          </FilterSelect>
          <FilterSelect label="To" htmlFor="activity-date-to">
            <input
              id="activity-date-to"
              type="date"
              className="filter-select"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setDeliveryPage(1);
              }}
            />
          </FilterSelect>
          <FilterSelect label="Search text / URL" htmlFor="activity-q" className="filter-select--wide">
            <input
              id="activity-q"
              type="search"
              className="filter-select"
              placeholder="URL, search name, error…"
              value={textFilter}
              onChange={(e) => {
                setTextFilter(e.target.value);
                setDeliveryPage(1);
              }}
            />
          </FilterSelect>
        </Toolbar>

        {initialLoading && deliveries.length === 0 ? (
          <div className="loading-inline" role="status">
            <span className="spinner" aria-hidden />
            Loading deliveries…
          </div>
        ) : (
          <>
            {canManageJobs ? (
              <BulkActionBar
                selectedCount={bulk.selectedCount}
                totalFiltered={deliveries.length}
                onClear={bulk.clearSelection}
                onSelectPage={() => bulk.togglePage(deliveryPageKeys)}
                onSelectFiltered={() => bulk.selectFiltered(deliveryPageKeys)}
                loading={bulkBusy}
              >
                <BulkActionButton
                  loading={bulkBusy}
                  disabled={bulk.selectedCount === 0}
                  onClick={() => void runBulkRetry()}
                >
                  Retry selected
                </BulkActionButton>
                <BulkActionButton
                  loading={bulkBusy}
                  variant="danger"
                  disabled={bulk.selectedCount === 0}
                  onClick={() => setShowCleanupModal(true)}
                >
                  Clear selected
                </BulkActionButton>
              </BulkActionBar>
            ) : null}
            <DataTable
            columns={deliveryColumns}
            rows={deliveries}
            getRowKey={deliveryRowKey}
            getRowClassName={(d) => (d.status === 'FAILED' ? 'row-failed' : undefined)}
            onRowClick={(d) => setDetailDeliveryId(d.id)}
            emptyState={deliveriesEmptyState}
            columnStorageKey="activity-deliveries"
            selection={
              canManageJobs
                ? {
                    isSelected: bulk.isSelected,
                    onToggleRow: bulk.toggleRow,
                    pageKeys: deliveryPageKeys,
                    pageAllSelected: bulk.pageAllSelected(deliveryPageKeys),
                    pageSomeSelected: bulk.pageSomeSelected(deliveryPageKeys),
                    onTogglePage: () => bulk.togglePage(deliveryPageKeys),
                  }
                : undefined
            }
            toolbar={{
              onRefresh: () => void autoRefresh.refresh(),
              rightSlot: <ExportButtons path="/exports/deliveries" params={deliveryExportParams} />,
            }}
          />
          <Pagination
            page={deliveryPage}
            pages={deliveryMeta.pages}
            total={deliveryMeta.total}
            onPage={setDeliveryPage}
          />
          </>
        )}
      </CollapsiblePanel>

      <CollapsiblePanel id="links" storageScope="activity-page" title="Recently discovered links" subtitle="Latest listings found across all searches" defaultExpanded={false}>
        <DataTable
          columns={linkColumns}
          rows={links}
          getRowKey={(l) => l.id}
          emptyState={linksEmptyState}
          columnStorageKey="activity-links"
          toolbar={{
            onRefresh: () => void autoRefresh.refresh(),
            rightSlot: <ExportButtons path="/exports/discovered-links" />,
          }}
        />
        <Pagination page={linkPage} pages={linkMeta.pages} total={linkMeta.total} onPage={setLinkPage} />
      </CollapsiblePanel>

      <CollapsiblePanel
        id="unified-activity"
        storageScope="activity-page"
        title="Unified activity log"
        subtitle="Deliveries, fetch runs, and pipeline events"
        defaultExpanded={false}
      >
        <ActivityLogPanel
          limit={20}
          showFilters
          exportPath="/exports/activity/logs"
          refreshSignal={autoRefresh.lastUpdatedAt}
        />
      </CollapsiblePanel>
      </AdminPageSections>

      {showCleanupModal && (
        <CleanupActivityConfirmModal
          selectedCount={bulk.selectedCount}
          alsoDeleteRelatedJobs={alsoDeleteRelatedJobs}
          onAlsoDeleteRelatedJobsChange={setAlsoDeleteRelatedJobs}
          onClose={() => setShowCleanupModal(false)}
          onConfirm={runBulkDelete}
        />
      )}

      {showHistoryCleanupModal && (
        <CleanupDeliveriesConfirmModal
          onClose={() => setShowHistoryCleanupModal(false)}
          onConfirm={runHistoryCleanup}
        />
      )}

      {detailDeliveryId && isUuid(detailDeliveryId) && (
        <DeliveryDetailDrawer
          deliveryId={detailDeliveryId}
          onClose={() => setDetailDeliveryId(null)}
          onRetry={retryDelivery}
          canManageJobs={canManageJobs}
          retrying={retryingId === detailDeliveryId}
        />
      )}

      {pendingClearDelivery ? (
        <AppConfirmDialog
          title="Remove delivery log?"
          lead={
            <>
              Permanently remove this delivery log from activity history. The discovered listing record is kept
              unless you remove related jobs separately.
            </>
          }
          confirmLabel="Remove log"
          submitting={clearDeliveryBusy}
          error={clearDeliveryError}
          onClose={() => {
            if (!clearDeliveryBusy) setPendingClearDelivery(null);
          }}
          onConfirm={async () => {
            setClearDeliveryBusy(true);
            setClearDeliveryError('');
            try {
              await clearSingleDelivery(pendingClearDelivery, false);
              setPendingClearDelivery(null);
            } catch (e) {
              setClearDeliveryError(formatApiError(e, 'Remove failed'));
            } finally {
              setClearDeliveryBusy(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}
